'use client'

import { useEffect, useState } from 'react'

import { cn } from '@/lib/utils'

const clockFormatters = new Map<string, Intl.DateTimeFormat>()
function formatClock(tz: string, now: Date): string {
  let fmt = clockFormatters.get(tz)
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: tz,
    })
    clockFormatters.set(tz, fmt)
  }
  return fmt.format(now)
}

/**
 * Live clock cell, minute precision: shows UTC; hovering anywhere on the
 * block rolls it up and IST rolls in from below — the same hand-off as the
 * mobile bar's CTA/MENU cell. The two layers share one grid cell, so the
 * block is sized by the wider layer and the swap never shifts layout.
 * Renders placeholders until mounted so the SSR HTML never carries a
 * timestamp that would mismatch on hydration; updates land exactly on
 * minute boundaries.
 */
export function WorldClock({ className }: { className?: string }) {
  const [now, setNow] = useState<Date | null>(null)

  useEffect(() => {
    setNow(new Date())
    let interval: ReturnType<typeof setInterval> | undefined
    const align = setTimeout(
      () => {
        setNow(new Date())
        interval = setInterval(() => setNow(new Date()), 60_000)
      },
      60_000 - (Date.now() % 60_000)
    )
    return () => {
      clearTimeout(align)
      if (interval) clearInterval(interval)
    }
  }, [])

  return (
    <div
      aria-label="Current time in UTC; hover for India"
      className={cn(
        // Whole cell is the hover target; eyebrow grammar, muted.
        'group/clock flex h-[var(--header-h)] items-center px-5',
        'text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground',
        className
      )}
    >
      <span className="grid overflow-hidden leading-none">
        <span className="col-start-1 row-start-1 tabular-nums transition-transform duration-300 group-hover/clock:-translate-y-full motion-reduce:transition-none">
          UTC {now ? formatClock('UTC', now) : '--:--'}
        </span>
        <span
          aria-hidden
          className="col-start-1 row-start-1 translate-y-full tabular-nums transition-transform duration-300 group-hover/clock:translate-y-0 motion-reduce:transition-none"
        >
          IST {now ? formatClock('Asia/Kolkata', now) : '--:--'}
        </span>
      </span>
    </div>
  )
}
